import { ArrowDown, ArrowUp } from "lucide-react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { Skeleton, Stack, useTheme } from "@mui/material";

import { UpliftRendererProps } from "./types";

export const UpliftRenderer = ({
  variantData,
  selectedBaselineId,
  isLoading,
  currency,
  selectedConfig,
}: UpliftRendererProps) => {
  const theme = useTheme();

  const formatUpliftValue = (value: number): string => {
    const sign = value > 0 ? "" : "+";
    return `${sign}${new Intl.NumberFormat(currency.locale || "en-US", {
      style: "decimal",
      maximumFractionDigits: 1,
      minimumFractionDigits: 1,
    }).format(value * 100)}%`;
  };

  const formatCiRange = (ciRange: number[] | undefined): string | undefined => {
    if (!ciRange || ciRange.length < 2) {
      return undefined;
    }
    const [low, high] = ciRange;
    const lowSign = low > 0 ? "+" : "";
    const highSign = high > 0 ? "+" : "";
    const lowFormatted = `${lowSign}${new Intl.NumberFormat(
      currency.locale || "en-US",
      {
        style: "decimal",
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      }
    ).format(low * 100)}%`;
    const highFormatted = `${highSign}${new Intl.NumberFormat(
      currency.locale || "en-US",
      {
        style: "decimal",
        maximumFractionDigits: 1,
        minimumFractionDigits: 1,
      }
    ).format(high * 100)}%`;
    return `${lowFormatted} to ${highFormatted}`;
  };

  const getUpliftColor = (upliftValue: number): string => {
    if (upliftValue === 0) {
      return theme.palette.text.primary;
    }

    const isPositive = upliftValue > 0;
    const coloring = selectedConfig.impactCalculationColoring;

    if (coloring === "neutral") {
      return theme.palette.text.primary;
    }

    if (coloring === "inverted") {
      return isPositive ? theme.igIconColors.error : theme.igIconColors.success;
    }

    // normal coloring
    return isPositive ? theme.igIconColors.success : theme.igIconColors.error;
  };

  return (
    <Stack spacing={10} sx={{ paddingY: 5 }}>
      {variantData.map((variant) => {
        const isBaseline = variant.variation_id === selectedBaselineId;
        const upliftValue = variant.uplift.value;
        const hasUplift = upliftValue !== null && upliftValue !== undefined;

        return (
          <Stack
            key={variant.variation_id}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <IgTypography>{variant.name}</IgTypography>
            {isLoading ? (
              <Skeleton variant="rectangular" width={100} height={24} />
            ) : isBaseline ? (
              <IgTypography fontSize={17} fontWeight={600}>
                n/a
              </IgTypography>
            ) : !hasUplift ? (
              <IgTypography
                variant="body1"
                fontWeight={600}
                sx={{ color: theme.palette.text.secondary }}
              >
                —
              </IgTypography>
            ) : (
              <Stack alignItems="flex-end">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  {upliftValue !== 0 &&
                    (upliftValue > 0 ? (
                      <ArrowUp size={14} color={getUpliftColor(upliftValue)} />
                    ) : (
                      <ArrowDown
                        size={14}
                        color={getUpliftColor(upliftValue)}
                      />
                    ))}
                  <IgTypography
                    variant="body1"
                    fontWeight={600}
                    sx={{ color: getUpliftColor(upliftValue) }}
                  >
                    {formatUpliftValue(upliftValue)}
                  </IgTypography>
                </Stack>
                {variant.uplift.ci_range && (
                  <IgTypography
                    variant="body3"
                    sx={{ color: theme.palette.text.primary }}
                  >
                    {formatCiRange(variant.uplift.ci_range)}
                  </IgTypography>
                )}
              </Stack>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};
